import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Send, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Payment", "Product", "Amazon", "Flipkart", "Website", "Domain", "Order", "Accounting", "Technical"];

export default function Support({ scope = "agent" }) {
  const qc = useQueryClient();
  const isAdmin = scope === "admin";
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ category: "Technical", subject: "", message: "" });
  const [reply, setReply] = useState("");

  const { data } = useQuery({
    queryKey: ["support", scope],
    queryFn: async () => (await api.get(isAdmin ? "/admin/support" : "/agent/support")).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/agent/support", form)).data,
    onSuccess: () => { toast.success("Ticket created — our team will respond"); setOpen(false); setForm({ category: "Technical", subject: "", message: "" }); qc.invalidateQueries({ queryKey: ["support"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const sendReply = useMutation({
    mutationFn: async () => api.post(isAdmin ? `/admin/support/${detail.id}/reply` : `/agent/support/${detail.id}/reply`, { message: reply, status: isAdmin ? "in_progress" : undefined }),
    onSuccess: (d) => { setReply(""); setDetail(d.data ?? d); qc.invalidateQueries({ queryKey: ["support"] }); toast.success("Reply sent"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => api.post(`/admin/support/${id}/reply`, { message: "", status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support"] }),
  });

  return (
    <div data-testid={`${scope}-support-page`}>
      <PageHeader title="Support Center" sub={isAdmin ? "Agent tickets — reply, assign status, resolve." : "Get help with payments, marketplaces, your store, orders or anything technical."} testid="support-title">
        {!isAdmin && (
          <Button data-testid="new-ticket-btn" onClick={() => setOpen(true)} className="bg-[#FF6B00] text-white hover:bg-[#E85D04]">
            <Plus size={14} /> New ticket
          </Button>
        )}
      </PageHeader>

      {!data?.items?.length ? <EmptyState title="No tickets" sub={isAdmin ? "Agent tickets will appear here." : "Need help? Create your first ticket."} /> : (
        <div className="space-y-2">
          {data.items.map((t) => (
            <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setDetail(t)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3 text-left shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-center gap-3">
                <LifeBuoy size={16} className="text-[#1D5BD8]" />
                <div>
                  <p className="text-sm font-semibold text-[#0F2440]">{t.subject}</p>
                  <p className="text-[10px] text-slate-500">{isAdmin && `${t.agent_name} · `}{t.category} · {fmtDate(t.created_at)} · {t.messages?.length || 0} messages</p>
                </div>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>New support ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ticket-category" className="border-slate-200 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-200 bg-white">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Subject" className="border-slate-200 bg-white" />
            <textarea data-testid="ticket-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Describe your issue…" rows={4}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" />
            <Button data-testid="ticket-submit" onClick={() => create.mutate()} disabled={create.isPending || !form.subject || !form.message}
              className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
              {create.isPending ? "Sending…" : "Create ticket"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg border-slate-200 bg-white text-[#0F2440]">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">{detail.subject} <StatusBadge status={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-3" data-testid="ticket-messages">
                {(detail.messages || []).map((m, i) => (
                  <div key={i} className={`rounded-lg px-3 py-2 text-xs ${m.from === "admin" ? "ml-8 bg-blue-50 text-[#0F2440]" : "mr-8 bg-white text-slate-600 shadow-sm"}`}>
                    <p className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">{m.from === "admin" ? "IndiaFast Support" : "You"} · {fmtDate(m.at)}</p>
                    {m.text || <span className="italic text-slate-400">Status updated</span>}
                  </div>
                ))}
              </div>
              {isAdmin && (
                <div className="flex gap-1.5">
                  {["in_progress", "waiting_for_agent", "resolved", "closed"].map((s) => (
                    <button key={s} data-testid={`ticket-status-${s}`} onClick={() => setStatus.mutate({ id: detail.id, status: s })}
                      className={`rounded-full border px-2.5 py-1 text-[9px] font-semibold capitalize ${detail.status === s ? "border-[#FF6B00] bg-orange-50 text-[#E85D04]" : "border-slate-200 text-slate-500"}`}>
                      {s.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…"
                  className="border-slate-200 bg-white" />
                <Button data-testid="ticket-reply-btn" onClick={() => sendReply.mutate()} disabled={!reply.trim() || sendReply.isPending}
                  className="bg-[#FF6B00] text-white hover:bg-[#E85D04]"><Send size={13} /></Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
